import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { StammdatenService } from '../../../services/stammdaten.service';
import { MultiSelectDialogComponent, MultiSelectDialogData } from '../multi-select-dialog/multi-select-dialog.component';
import { KontrollterminService } from '../../../services/kontrolltermin.service';
import { ObjektStrukturService } from '../../../services/objekt-struktur.service';
import { MultiSelectItem } from '../../../shared/interfaces/multi-select.interface';
import { getSession } from '../../../auth/storage';

export interface IndikatorEditData {
  indikator: any;
  befallAmpelOptions: any[];
  currentStatus?: string; // Add current kontrolltermin status
  isViewMode?: boolean; // Add view mode flag
}

@Component({
  selector: 'app-dialog-indikator-edit',
  templateUrl: './dialog-indikator-edit.component.html',
  styleUrls: ['./dialog-indikator-edit.component.css']
})
export class DialogIndikatorEditComponent implements OnInit {
  indikatorForm: FormGroup;
  loading = false;
  
  // View/Edit mode properties
  isViewMode = false;
  isEditMode = false;

  // Multi-select data
  massnahmenItems: MultiSelectItem[] = [];
  abweichungenItems: MultiSelectItem[] = [];
  artikelItems: MultiSelectItem[] = [];
  
  selectedMassnahmen: MultiSelectItem[] = [];
  selectedAbweichungen: MultiSelectItem[] = [];
  selectedArtikel: MultiSelectItem[] = [];

  massnahmenLoading = false;
  abweichungenLoading = false;
  artikelLoading = false;

  // Schwellenwert (editable, standalone from form)
  schwellenwertValue: number = 0;

  // Computed status based on wert vs schwellenwert
  computedStatusColor = '#6b7280';
  computedStatusId: '1' | '2' | '3' | '' = '';

  // Preview information
  previewInfo = {
    willBeCheckedBy: '',
    willBeCheckedAt: '',
    isFirstSave: false
  };

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<DialogIndikatorEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IndikatorEditData,
    private stammdatenService: StammdatenService,
    private kontrollterminService: KontrollterminService,
    private objektStrukturService: ObjektStrukturService,
    private dialog: MatDialog
  ) {
    // Round wert to integer (no decimals)
    const initialWert = this.data.indikator.wert != null ? Math.round(Number(this.data.indikator.wert)) : 0;

    // Initialize schwellenwert from RoomIndikator
    const rawSchwellenwert = this.data.indikator.indikator?.schwellenwert;
    this.schwellenwertValue = rawSchwellenwert != null ? Math.round(Number(rawSchwellenwert)) : 0;
    
    this.indikatorForm = this.fb.group({
      wert: [initialWert, [Validators.required]],
      problem: [''], // Will be set from selectedAbweichungen
      massnahme: [''], // Will be set from selectedMassnahmen  
      interne_notiz: [''], // Will be set from selectedArtikel
      anmerkung: [this.data.indikator.anmerkung || '']
    });
  }

  /**
   * Round wert to integer on change
   */
  roundWert(): void {
    const wertControl = this.indikatorForm.get('wert');
    if (wertControl) {
      const val = Number(wertControl.value);
      if (isFinite(val)) {
        wertControl.setValue(Math.round(val));
      }
    }
  }

  formatSchwellenwert(value: any): string {
    if (value === null || value === undefined || value === '') return '-';
    const num = Number(value);
    return isFinite(num) ? Math.round(num).toString() : '-';
  }

  /**
   * Get the indikator type/code from the base Indikator (e.g. "Maus", "Schabe")
   */
  getIndikatorCode(): string {
    return this.data.indikator?.indikator?.indikator?.code
      || this.data.indikator?.indikator?.code
      || '';
  }

  /**
   * Round schwellenwert and recompute status
   */
  onSchwellenwertChange(): void {
    const val = Number(this.schwellenwertValue);
    if (isFinite(val)) {
      this.schwellenwertValue = Math.round(val);
    }
    // Recompute status color since schwellenwert changed
    this.updateComputedStatus(this.indikatorForm.get('wert')?.value);
  }

  ngOnInit(): void {
    console.log('🔧 Dialog opened for indikator:', this.data.indikator);
    console.log('🔧 Current status:', this.data.currentStatus);
    console.log('🔧 Is view mode:', this.data.isViewMode);
    
    // Set initial mode based on data
    this.isViewMode = this.data.isViewMode || this.data.currentStatus === 'freigabe' || this.data.currentStatus === 'abgeschlossen';
    this.isEditMode = !this.isViewMode;
    
    console.log('🔧 Final mode - View:', this.isViewMode, 'Edit:', this.isEditMode);
    
    // Update preview information
    this.updatePreviewInfo();
    
    this.loadMultiSelectData();
    this.parseExistingSelections();
    // Initialize and reactively compute status color
    this.updateComputedStatus(this.indikatorForm.get('wert')?.value);
    this.indikatorForm.get('wert')?.valueChanges.subscribe((v) => this.updateComputedStatus(v));
  }

  private getSchwellenwert(): number {
    const num = Number(this.schwellenwertValue);
    return isFinite(num) ? num : 0;
  }

  private updateComputedStatus(rawValue: any): void {
    const wert = Number(rawValue);
    const schwellenwert = this.getSchwellenwert();
    if (!isFinite(wert)) {
      this.computedStatusId = '';
      this.computedStatusColor = '#6b7280';
      return;
    }
    if (wert === 0) {
      this.computedStatusId = '3'; // green / OK
      this.computedStatusColor = this.getBefallStatusColor('3');
    } else if (wert > schwellenwert) {
      this.computedStatusId = '1'; // red / critical
      this.computedStatusColor = this.getBefallStatusColor('1');
    } else {
      this.computedStatusId = '2'; // orange / warning
      this.computedStatusColor = this.getBefallStatusColor('2');
    }
  }

  getIndikatorDisplayNumber(): string {
    const n = this.data.indikator?.indikator?.nummer
      ?? this.data.indikator?.indikator_nummer
      ?? this.data.indikator?.nummer
      ?? this.data.indikator?.indikator_id;
    const num = Number(n);
    if (isFinite(num)) {
      return '#' + num.toString().padStart(3, '0');
    }
    return '#—';
  }

  private loadMultiSelectData(): void {
    // Load Massnahmen
    this.massnahmenLoading = true;
    this.stammdatenService.getMassnahmen().subscribe({
      next: (massnahmen) => {
        this.massnahmenItems = massnahmen.map(m => ({
          id: m.id.toString(),
          name: m.beschreibung,
          description: `Sortierung: ${m.sortierung}`
        }));
        this.massnahmenLoading = false;
      },
      error: (error) => {
        console.error('Error loading massnahmen:', error);
        this.massnahmenLoading = false;
      }
    });

    // Load Abweichungen
    this.abweichungenLoading = true;
    this.stammdatenService.getAbweichungen().subscribe({
      next: (abweichungen) => {
        this.abweichungenItems = abweichungen.map(a => ({
          id: a.id.toString(),
          name: a.caption,
          description: a.description
        }));
        this.abweichungenLoading = false;
      },
      error: (error) => {
        console.error('Error loading abweichungen:', error);
        this.abweichungenLoading = false;
      }
    });

    // Load Artikel
    this.artikelLoading = true;
    this.stammdatenService.getArtikels().subscribe({
      next: (artikel) => {
        this.artikelItems = artikel.map(a => ({
          id: a.id.toString(),
          name: a.beschreibung,
          description: `Nummer: ${a.nummer}`
        }));
        this.artikelLoading = false;
      },
      error: (error) => {
        console.error('Error loading artikel:', error);
        this.artikelLoading = false;
      }
    });
  }

  private parseExistingSelections(): void {
    // Parse existing JSON data from the indikator
    try {
      if (this.data.indikator.massnahme) {
        let massnahmenData;
        if (typeof this.data.indikator.massnahme === 'string') {
          massnahmenData = JSON.parse(this.data.indikator.massnahme);
        } else if (Array.isArray(this.data.indikator.massnahme)) {
          massnahmenData = this.data.indikator.massnahme;
        }
        
        if (Array.isArray(massnahmenData)) {
        this.selectedMassnahmen = massnahmenData.map((m: any) => ({
          id: m.id,
          name: m.name,
          description: m.description
        }));
        }
      }
    } catch (e) {
      console.log('No existing massnahmen data or invalid JSON:', e);
      this.selectedMassnahmen = [];
    }

    try {
      if (this.data.indikator.problem) {
        let abweichungenData;
        if (typeof this.data.indikator.problem === 'string') {
          abweichungenData = JSON.parse(this.data.indikator.problem);
        } else if (Array.isArray(this.data.indikator.problem)) {
          abweichungenData = this.data.indikator.problem;
        }
        
        if (Array.isArray(abweichungenData)) {
        this.selectedAbweichungen = abweichungenData.map((a: any) => ({
          id: a.id,
          name: a.name,
          description: a.description
        }));
        }
      }
    } catch (e) {
      console.log('No existing abweichungen data or invalid JSON:', e);
      this.selectedAbweichungen = [];
    }

    try {
      if (this.data.indikator.interne_notiz) {
        let artikelData;
        if (typeof this.data.indikator.interne_notiz === 'string') {
          artikelData = JSON.parse(this.data.indikator.interne_notiz);
        } else if (Array.isArray(this.data.indikator.interne_notiz)) {
          artikelData = this.data.indikator.interne_notiz;
        }
        
        if (Array.isArray(artikelData)) {
        this.selectedArtikel = artikelData.map((a: any) => ({
          id: a.id,
          name: a.name,
          description: a.description,
          amount: a.amount || 1 // Preserve amount, default to 1 if not specified
        }));
        }
      }
    } catch (e) {
      console.log('No existing artikel data or invalid JSON:', e);
      this.selectedArtikel = [];
    }
  }

  /**
   * Switch from view mode to edit mode
   */
  switchToEditMode(): void {
    this.isViewMode = false;
    this.isEditMode = true;
    console.log('🔧 Switched to edit mode');
  }

  /**
   * Switch from edit mode to view mode
   */
  switchToViewMode(): void {
    this.isViewMode = true;
    this.isEditMode = false;
    console.log('🔧 Switched to view mode');
  }

  /**
   * Close dialog without saving
   */
  onClose(): void {
    this.dialogRef.close();
  }

  /**
   * Check if the indikator has actually been checked by a real user
   */
  isActuallyChecked(): boolean {
    const checkedBy = this.data.indikator.checked_by;
    const hasValidCheckedBy = checkedBy && checkedBy !== 0 && checkedBy !== '0' && checkedBy !== null && checkedBy !== 'null';
    const hasCheckedDate = this.data.indikator.checked && this.data.indikator.checked !== null && this.data.indikator.checked !== '';
    return !!hasValidCheckedBy && !!hasCheckedDate;
  }

  /**
   * Get formatted checked date
   */
  getFormattedCheckedDate(): string {
    if (!this.data.indikator.checked) return '-';
    const date = new Date(this.data.indikator.checked);
    return date.toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  /**
   * Get checked by user name
   */
  getCheckedByUserName(): string {
    // Not checked
    if (!this.data.indikator.checked_by || 
        this.data.indikator.checked_by === 0 || 
        this.data.indikator.checked_by === '0') {
      return 'Noch nicht geprüft';
    }

    // Superuser
    if (this.data.indikator.checked_by === 999 || this.data.indikator.checked_by === '999') {
      return 'System Administrator';
    }

    // Use checked_by_name from API (resolved by the model's toArray)
    if (this.data.indikator.checked_by_name && 
        this.data.indikator.checked_by_name !== 'Noch nicht geprüft') {
      return this.data.indikator.checked_by_name;
    }

    // Handle case where checked_by is an object (the full Mitarbeiter data)
    if (typeof this.data.indikator.checked_by === 'object' && this.data.indikator.checked_by !== null) {
      const checkedBy = this.data.indikator.checked_by;
      const name = checkedBy.full_name || 
                   checkedBy.fullName ||
                   `${checkedBy.vorname || ''} ${checkedBy.nachname || ''}`.trim() ||
                   checkedBy.display_name ||
                   checkedBy.name;
      if (name) return name;
    }

    // Check if we have the checkedBy relationship loaded (fallback)
    if (this.data.indikator.checkedBy) {
      const checkedBy = this.data.indikator.checkedBy;
      const name = checkedBy.full_name || 
                   checkedBy.fullName ||
                   `${checkedBy.vorname || ''} ${checkedBy.nachname || ''}`.trim() ||
                   checkedBy.display_name ||
                   checkedBy.name;
      if (name) return name;
    }

    // Final fallback: show ID
    return `Mitarbeiter #${this.data.indikator.checked_by}`;
  }

  /**
   * Get user name by ID (for preview purposes)
   */
  private getUserNameById(userId: number | string): string {
    // Handle superuser case
    if (userId === 999 || userId === '999') {
      return 'System Administrator';
    }
    
    // Handle case where userId might be an object (shouldn't happen, but just in case)
    if (typeof userId === 'object') {
      console.log('⚠️ getUserNameById received object instead of ID:', userId);
      return 'Unknown User';
    }
    
    // Check if this is the current user and we can get the name from session
    const session = getSession();
    console.log('🔍 getUserNameById Debug:', {
      userId: userId,
      sessionFid: session?.fid,
      sessionDienstname: session?.dienstname,
      isCurrentUser: session && session.fid == userId
    });
    
    if (session && session.fid == userId && session.dienstname) {
      console.log('✅ Using session dienstname:', session.dienstname);
      return session.dienstname;
    }
    
    // Fallback to showing the ID
    return `Mitarbeiter ${userId}`;
  }

  /**
   * Update preview information for the dialog
   */
  private updatePreviewInfo(): void {
    const session = getSession();
    const foreignId = session?.fid;
    
    console.log('🔍 Preview Info Debugging:', {
      session: session,
      userid: session?.userid,
      fid: session?.fid,
      foreignId: foreignId,
      sessionKeys: Object.keys(session || {}),
      fullSession: JSON.stringify(session, null, 2)
    });
    
    // Use fid if available, otherwise fall back to userid
    const effectiveId = foreignId || session?.userid;
    
    // Determine if this is a first save - use isActuallyChecked() for consistent 0/'0' handling
    this.previewInfo.isFirstSave = !this.isActuallyChecked();
    
    if (this.previewInfo.isFirstSave) {
      // Preview what will be saved
      const now = new Date();
      this.previewInfo.willBeCheckedAt = now.toLocaleString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      
      // Get user name for preview
      this.previewInfo.willBeCheckedBy = this.getUserNameById(effectiveId);
    } else {
      // Show existing saved values
      this.previewInfo.willBeCheckedAt = this.getFormattedCheckedDate();
      const checkedByName = this.getCheckedByUserName();
      console.log('🔍 Setting previewInfo.willBeCheckedBy:', {
        checkedByName: checkedByName,
        type: typeof checkedByName
      });
      this.previewInfo.willBeCheckedBy = checkedByName;
    }
  }

  onSave(): void {
    if (this.indikatorForm.valid) {
      this.loading = true;
      const formValue = this.indikatorForm.getRawValue();
      
      // Convert multi-select selections to JSON
      formValue.massnahme = JSON.stringify(this.selectedMassnahmen);
      formValue.problem = JSON.stringify(this.selectedAbweichungen);
      // Include amounts when saving artikel
      formValue.interne_notiz = JSON.stringify(this.selectedArtikel.map(a => ({
        id: a.id,
        name: a.name,
        description: a.description,
        amount: a.amount || 1
      })));
      
      // NOTE: befall_status_id and befall are now calculated by the API based on wert vs schwellenwert
      // Do not send these values from the frontend - let the API calculate them correctly

      // Determine if this is the first save - use isActuallyChecked() for consistent 0/'0' handling
      const isFirstSave = !this.isActuallyChecked();
      console.log('🔍 Debug isFirstSave:', {
        checked: this.data.indikator.checked,
        isFirstSave: isFirstSave,
        checked_by: this.data.indikator.checked_by
      });
      
      // Update the indikator object with form values
            const updateData: any = {
              ...formValue,
              // Explicitly remove befall_status_id and befall - let API calculate them
              updated_at: new Date().toISOString() // Always set updated timestamp (this is a timestamp field, so full datetime is OK)
            };
            
            // Remove befall_status_id and befall from payload - API will calculate them
            delete updateData.befall_status_id;
            delete updateData.befall;
      
            // Only set checked date and user on first save
            if (isFirstSave) {
              // Send full ISO datetime since the database column is now 'datetime' type
              updateData.checked = new Date().toISOString();
              
              // Get foreign ID from session - this is the correct ID to use
              const session = getSession();
              const foreignId = session?.fid;
              
              console.log('🔍 Session debugging:', {
                session: session,
                userid: session?.userid,        // Account ID (for reference)
                fid: session?.fid,              // Foreign ID (mitarbeiter_id or 999 for superuser)
                foreignId: foreignId,
                sessionKeys: Object.keys(session || {}),
                fullSession: JSON.stringify(session, null, 2)
              });
              
              // Check if fid is undefined or null
              if (!foreignId) {
                console.error('❌ FID is not available in session! Using fallback to userid');
                console.log('Available session keys:', Object.keys(session || {}));
                console.log('Session userid:', session?.userid);
                console.log('Session fid:', session?.fid);
              }
              
              // Set checked_by to foreign ID (this handles both regular users and superusers)
              // If fid is not available, fall back to userid (for backward compatibility)
              if (foreignId) {
                updateData.checked_by = parseInt(String(foreignId), 10);
                console.log('✅ Using FID for checked_by:', foreignId);
              } else {
                // Fallback to userid if fid is not available
                const fallbackId = session?.userid;
                updateData.checked_by = fallbackId ? parseInt(String(fallbackId), 10) : null;
                console.log('⚠️ FID not available, using userid as fallback:', fallbackId);
              }
            }
      
      Object.assign(this.data.indikator, updateData);
      
      // Create a clean payload with only the fields we want to update
      const cleanPayload: any = {
        wert: updateData.wert,
        befall: updateData.befall,
        befall_status_id: updateData.befall_status_id,
        problem: updateData.problem,
        massnahme: updateData.massnahme,
        interne_notiz: updateData.interne_notiz,
        anmerkung: updateData.anmerkung,
        updated_at: updateData.updated_at
      };
      
      // Add checked fields only on first save
      if (isFirstSave) {
        cleanPayload.checked = updateData.checked;
        cleanPayload.checked_by = updateData.checked_by;
        console.log('✅ Adding checked fields to payload:', {
          checked: updateData.checked,
          checked_by: updateData.checked_by
        });
      } else {
        console.log('❌ Not adding checked fields (not first save)');
      }
      
            // Make actual API call to save the indikator with clean payload
            console.log('🚀 Making API call to update indikator:', this.data.indikator.id);
            console.log('🚀 Payload being sent:', JSON.stringify(cleanPayload, null, 2));
            this.kontrollterminService.updateKontrollIndikator(this.data.indikator.id, cleanPayload).subscribe({
        next: (updatedIndikator) => {
          this.loading = false;
          console.log('✅ Indikator saved successfully - API Response:', {
            id: updatedIndikator.id,
            checked: updatedIndikator.checked,
            checked_by: updatedIndikator.checked_by,
            wert: updatedIndikator.wert,
            updated_at: updatedIndikator.updated_at,
            full_response: updatedIndikator
          });
          console.log('✅ Full API response object:', JSON.stringify(updatedIndikator, null, 2));
          
          // Preserve checkedBy relationship from API response if available
          if (updatedIndikator.checkedBy) {
            console.log('✅ API response includes checkedBy relationship:', updatedIndikator.checkedBy);
            // Relationship is already loaded from API, use it as-is
          } else if (updatedIndikator.checked_by && typeof updatedIndikator.checked_by === 'number') {
            // If API didn't return checkedBy relationship, try to preserve original or create from session
            const session = getSession();
            
            // First, try to preserve the original checkedBy from dialog data if it matches
            if (this.data.indikator.checkedBy && 
                this.data.indikator.checkedBy.id === updatedIndikator.checked_by) {
              console.log('✅ Preserving original checkedBy from dialog data:', this.data.indikator.checkedBy);
              (updatedIndikator as any).checkedBy = this.data.indikator.checkedBy;
            } 
            // If original doesn't match (different user), try to get from session if it's the current user
            else if (session && session.fid == updatedIndikator.checked_by && session.dienstname) {
              // Create a mock checkedBy object with the user info from session
              const mockCheckedBy = {
                id: updatedIndikator.checked_by,
                full_name: session.dienstname,
                vorname: session.dienstname.split(' ')[0] || '',
                nachname: session.dienstname.split(' ').slice(1).join(' ') || '',
                display_name: session.dienstname
              };
              
              // Add the mock object as a separate property
              (updatedIndikator as any).checkedBy = mockCheckedBy;
              console.log('✅ Added checkedBy object with session data:', mockCheckedBy);
            } else {
              // If we can't determine the name, at least preserve the checked_by ID
              console.log('⚠️ Could not determine checkedBy name, preserving checked_by ID:', updatedIndikator.checked_by);
            }
          }
          
          // Also update schwellenwert on RoomIndikator if it changed
          const originalSchwellenwert = Math.round(Number(this.data.indikator.indikator?.schwellenwert) || 0);
          if (this.schwellenwertValue !== originalSchwellenwert && this.data.indikator.indikator?.id) {
            console.log('🚀 Schwellenwert changed, updating RoomIndikator:', {
              roomIndikatorId: this.data.indikator.indikator.id,
              oldValue: originalSchwellenwert,
              newValue: this.schwellenwertValue
            });
            this.objektStrukturService.updateRoomIndikator(this.data.indikator.indikator.id, {
              schwellenwert: String(this.schwellenwertValue)
            }).subscribe({
              next: () => {
                console.log('✅ Schwellenwert updated on RoomIndikator');
                // Update the local data so it reflects in the UI
                if (this.data.indikator.indikator) {
                  this.data.indikator.indikator.schwellenwert = this.schwellenwertValue;
                }
              },
              error: (err: any) => console.error('❌ Error updating schwellenwert:', err)
            });
          }

          this.dialogRef.close(updatedIndikator);
        },
            error: (error) => {
        this.loading = false;
              console.error('❌ Error saving indikator:', error);
              console.error('❌ Error status:', error.status);
              console.error('❌ Error message:', error.message);
              console.error('❌ Error body:', error.error);
              console.error('❌ Full error object:', JSON.stringify(error, null, 2));
              // Still close the dialog but with the original data
        this.dialogRef.close(this.data.indikator);
            }
      });
    }
  }

  onMassnahmenChange(selectedItems: MultiSelectItem[]): void {
    this.selectedMassnahmen = selectedItems;
  }

  onAbweichungenChange(selectedItems: MultiSelectItem[]): void {
    this.selectedAbweichungen = selectedItems;
  }

  onArtikelChange(selectedItems: MultiSelectItem[]): void {
    // Preserve amounts when items are added/removed
    const existingAmounts = new Map(this.selectedArtikel.map(a => [a.id, a.amount || 1]));
    this.selectedArtikel = selectedItems.map(item => ({
      ...item,
      amount: existingAmounts.get(item.id) || item.amount || 1
    }));
  }

  updateArtikelAmount(artikel: MultiSelectItem): void {
    // Ensure amount is at least 1
    if (!artikel.amount || artikel.amount < 1) {
      artikel.amount = 1;
    }
    // Update the artikel in the array
    const index = this.selectedArtikel.findIndex(a => a.id === artikel.id);
    if (index !== -1) {
      this.selectedArtikel[index] = { ...artikel };
    }
  }

  /**
   * Open Massnahmen selection dialog
   */
  openMassnahmenDialog(): void {
    const dialogData: MultiSelectDialogData = {
      title: 'Maßnahmen auswählen',
      items: this.massnahmenItems,
      selectedItems: [...this.selectedMassnahmen],
      showAmounts: false
    };

    const dialogRef = this.dialog.open(MultiSelectDialogComponent, {
      width: '550px',
      maxHeight: '80vh',
      data: dialogData,
      autoFocus: false
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== null && result !== undefined) {
        this.selectedMassnahmen = result;
      }
    });
  }

  /**
   * Open Artikel/Produkt selection dialog
   */
  openArtikelDialog(): void {
    // Merge existing amounts into selectedItems for the dialog
    const selectedWithAmounts = this.selectedArtikel.map(a => ({ ...a }));

    const dialogData: MultiSelectDialogData = {
      title: 'Produkt auswählen',
      items: this.artikelItems,
      selectedItems: selectedWithAmounts,
      showAmounts: true
    };

    const dialogRef = this.dialog.open(MultiSelectDialogComponent, {
      width: '550px',
      maxHeight: '80vh',
      data: dialogData,
      autoFocus: false
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== null && result !== undefined) {
        this.selectedArtikel = result;
      }
    });
  }

  /**
   * Remove a single Massnahme chip
   */
  removeMassnahme(item: MultiSelectItem): void {
    this.selectedMassnahmen = this.selectedMassnahmen.filter(m => m.id !== item.id);
  }

  /**
   * Remove a single Artikel chip
   */
  removeArtikel(item: MultiSelectItem): void {
    this.selectedArtikel = this.selectedArtikel.filter(a => a.id !== item.id);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  getBefallColor(befall: any): string {
    if (befall === 1) return 'bg-red-100 text-red-800';
    if (befall === 0) return 'bg-green-100 text-green-800';
    return 'bg-gray-100 text-gray-800';
  }

  getBefallText(befall: any): string {
    if (befall === 1) return 'Ja';
    if (befall === 0) return 'Nein';
    return '-';
  }

  getBefallStatusColor(statusId: any): string {
    const statusMap: { [key: string]: string } = {
      '1': '#ef4444', // red
      '2': '#f59e0b', // yellow
      '3': '#10b981'  // green
    };
    return statusMap[statusId] || '#6b7280';
  }

  getBefallStatusText(statusId: any): string {
    const statusMap: { [key: string]: string } = {
      '1': 'Kritisch',
      '2': 'Warnung',
      '3': 'OK'
    };
    return statusMap[statusId] || '-';
  }
}
